import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  selectAggregateSignal,
  selectLiveSyncConnected,
  type AggregateKey,
} from '../lib';

/** Slow backstop poll once the event stream is healthy (events drive refetches). */
export const HEARTBEAT_DELAY_MS = 45_000;

interface AggregateRefetch {
  getDelay: () => number;
  refetchSignal: number;
}

/**
 * Wires one command-center aggregate to `usePollingQuery`: its refetch signal,
 * plus a heartbeat cadence while connected (falling back to `baseGetDelay`'s
 * timer poll when the socket is down). Reads the `liveSync` slice via the shared
 * selectors, so the api layer never imports up into nop-events.
 */
export const useAggregateRefetch = (
  key: AggregateKey,
  baseGetDelay: () => number,
): AggregateRefetch => {
  const connected = useSelector(selectLiveSyncConnected);
  const refetchSignal = useSelector(selectAggregateSignal(key));

  const getDelay = useMemo(
    () => (connected ? () => HEARTBEAT_DELAY_MS : baseGetDelay),
    [connected, baseGetDelay],
  );

  return { getDelay, refetchSignal };
};
